import merge from 'lodash/merge';

import { createDashHttpClient } from '@/apis/dash.http';
import { getUrlPathByPlatform } from '@/app/community/utils/comments';
import { env } from '@/env';

export const http = createDashHttpClient(env.communityApiUrl, {
  camelizeKeys: true,
});

export function saveCommunityInteractionTicket(
  {
    communityInteractionId,
    source,
    sourceId,
    sourceCreatedAt,
    sourceUpdatedAt,
    label,
    status,
    messageId,
  },
  config = {},
) {
  return http.put(
    `/community_interactions/${communityInteractionId}/tickets/${sourceId}`,
    {
      source,
      sourceId,
      sourceCreatedAt,
      sourceUpdatedAt,
      label,
      status,
      messageId,
    },
    config,
  );
}

export function getCommentInteractionList({ platform, brandId, mediaId, limit = 12 }, config = {}) {
  const params = {
    brandId,
    mediaId,
    limit,
  };
  const url = `/${getUrlPathByPlatform(platform)}`;
  return http.get(
    url,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getCommentInteractionDetail(
  { platform, communityInteractionId, returnParent },
  config = {},
) {
  const params = {
    returnParent,
  };
  return http.get(
    `/${getUrlPathByPlatform(platform)}/${communityInteractionId}`,
    merge(
      {
        params,
      },
      config,
    ),
  );
}

export function getFacebookComment({ id }, config = {}) {
  return http.get(`/facebook_comments/${id}`, config);
}

export function getInstagramComment({ id }, config = {}) {
  return http.get(`/instagram_comments/${id}`, config);
}

export function getTikTokComment({ id }, config = {}) {
  return http.get(`/tiktok_comments/${id}`, config);
}

export function getTwitterComment({ id }, config = {}) {
  return http.get(`/tweets/${id}`, config);
}

export function getYoutubeComment({ id }, config = {}) {
  return http.get(`/youtube_comments/${id}`, config);
}

export function getFacebookCommentsCsv({ socketId, mediaId, brandId }, config = {}) {
  return http.post(`/facebook_comments/csv`, { socketId, mediaId, brandId }, config);
}

export function getInstagramCommentsCsv({ socketId, mediaId, brandId }, config = {}) {
  return http.post(`/instagram_comments/csv`, { socketId, mediaId, brandId }, config);
}

export function getTikTokCommentsCsv({ socketId, mediaId, brandId }, config = {}) {
  return http.post(`/tiktok_comments/csv`, { socketId, mediaId, brandId }, config);
}

export function getTwitterCommentsCsv({ socketId, mediaId, brandId }, config = {}) {
  return http.post(`/tweets/csv`, { socketId, mediaId, brandId }, config);
}

export function getYoutubeCommentsCsv({ socketId, mediaId, brandId }, config = {}) {
  return http.post(`/youtube_comments/csv`, { socketId, mediaId, brandId }, config);
}

export function getAllMessagesCsv(payload, config = {}) {
  return http.post(`/community_interactions/csv`, payload, config);
}

/*
   Interaction rules endpoints
*/
export async function getInteractionRulesForBrands(brandIds, config = {}) {
  const url = `/interaction_rules`;
  const params = { brandIds };
  return http.get(url, merge({ params }, config));
}
export async function createInteractionRule(brandId, payload, config = {}) {
  const url = `/brands/${brandId}/interaction_rules`;
  return http.post(url, payload, config);
}
export async function updateInteractionRule(brandId, ruleId, payload, config = {}) {
  const url = `/brands/${brandId}/interaction_rules/${ruleId}`;

  return http.patch(url, payload, config);
}
export async function deleteInteractionRule(brandId, ruleId, config = {}) {
  const url = `/brands/${brandId}/interaction_rules/${ruleId}`;
  return http.delete(url, config);
}
export async function deleteInteractionRulesHistory(brandId, ruleExecutionHistoryId, config = {}) {
  const url = `/brands/${brandId}/interaction_rules_history/${ruleExecutionHistoryId}`;
  return http.delete(url, config);
}

export async function getRuleFilterMatchCount(brandId, ruleFilters, config = {}) {
  const url = `/brands/${brandId}/interaction_rules/match_count`;
  return http.post(url, { ruleFilters }, config);
}

export async function getInteractionRulesPreview(brandId, filters, config = {}) {
  const url = `/brands/${brandId}/interaction_rules/preview`;
  return http.post(url, { filters }, config);
}

/*
   AI Category API
*/
export async function getAiCategoriesForBrands(brandIds, config = {}) {
  const params = { brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds };
  return http.get('/ai_categories', merge({ params }, config));
}

export async function createAiCategory({ brandId, name, prompt }, config = {}) {
  const url = `/brands/${brandId}/ai_category`;
  const payload = { name, prompt };
  return http.post(url, payload, config);
}

export async function deleteAiCategory(aiCategoryId, config = {}) {
  return http.delete(`/ai_categories/${aiCategoryId}`, config);
}

export default {
  async bulkUpdateCommunityInteractionsByMedia({ brandId, mediaId, ...updateFields }, config = {}) {
    const data = { brand_id: brandId, media_id: mediaId };
    if ('isArchived' in updateFields) {
      data.is_archived = updateFields.isArchived;
    }
    if ('isRead' in updateFields) {
      data.is_read = updateFields.isRead;
    }
    return http.post('/community_interactions/bulk_update_by_media', data, config);
  },
  async updatePostEnabled({ brandId, mediaId, commentDisabled }, config = {}) {
    const url = `/posts/${brandId}/${mediaId}`;
    return http.patch(url, { commentDisabled }, config);
  },
  async createTweet({ brandId, parentTweetId, text, isQuote }, config = {}) {
    const url = `/tweets`;
    return http.post(url, { brandId, parentTweetId, text, isQuote }, config);
  },
  async patchTweet(
    { communityInteractionId, isHidden = null, isLiked = null, isRetweeted = null },
    config,
  ) {
    const url = `/tweets/${communityInteractionId}`;
    const payload = { isHidden, isRetweeted, isLiked };
    const filteredPayload = Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v !== null),
    );
    return http.patch(url, filteredPayload, config);
  },
  async createDM({ conversationId, platform, text, uuid }, config = {}) {
    const url = `/conversations/${platform}/${conversationId}/messages`;
    const payload = { text, uuid };
    return http.post(url, payload, config);
  },
  async getPlatformAccount({ platform, brandId }, config) {
    const url = `/conversations/${platform}/accounts/${brandId}`;
    return http.get(url, config);
  },
  async getPlatformMessages(
    { platform, id, limit, createdOnOrAfter, createdOnOrBefore },
    config = {},
  ) {
    const url = `/conversations/${platform}/${id}/messages`;
    return http.get(
      url,
      merge(
        {
          params: {
            limit,
            createdOnOrAfter,
            createdOnOrBefore,
          },
        },
        config,
      ),
    );
  },
  async updateDM({ conversationId, platform, messageId, payload }, config = {}) {
    const url = `/conversations/${platform}/${conversationId}/messages/${messageId}`;
    return http.patch(url, payload, config);
  },

  //
  // PRESET REPLY API
  //
  async getPresetReplyList({ brandId, type = undefined }, config) {
    const url = `/preset_replies`;
    const params = { brandId, type };
    return http.get(url, merge({ params }, config));
  },
  async createPresetReply({ payload }, config) {
    const url = `/preset_replies`;
    return http.post(url, payload, config);
  },
  async updatePresetReply({ replyId, payload }, config) {
    const url = `/preset_replies/${replyId}`;
    return http.patch(url, payload, config);
  },
  async deletePresetReply(replyId, config) {
    const url = `/preset_replies/${replyId}`;
    return http.delete(url, config);
  },
  async getUnreadCommunityInteractionCountsV2(payload, config) {
    const url = `/community_interactions/unread_interaction_counts`;
    return http.post(url, payload, { ...config, camelizeKeys: false });
  },
  async getCommunityInteractionCounts(payload, config) {
    const url = `/community_interactions/counts`;
    return http.post(url, payload, { ...config, camelizeKeys: false });
  },

  //
  // COMMENT API
  //
  async getCommentList({ next, brandId, mediaId, limit, platform }, config = {}) {
    const url =
      next ||
      `/${getUrlPathByPlatform(platform)}?brand_id=${brandId}&media_id=${mediaId}&limit=${limit}`;
    return http.get(url, config);
  },
  async getCommentDetail(id, returnParent, platform, config = {}) {
    const url = returnParent
      ? `/${getUrlPathByPlatform(platform)}/${id}?return_parent=${returnParent}`
      : `/${getUrlPathByPlatform(platform)}/${id}`;
    return http.get(url, config);
  },
  async updateCommentRequest({ commentId, platform, payload }, config = {}) {
    const url = `/${getUrlPathByPlatform(platform)}/${commentId}`;
    return http.patch(url, payload, config);
  },
  async deleteComment(commentId, platform, softDelete, config = {}) {
    const url = `/${getUrlPathByPlatform(platform)}/${commentId}${
      softDelete ? '?soft_delete_only=true' : ''
    }`;
    return http.delete(url, config);
  },
  async createComment(platform, payload, config = {}) {
    const url = `/${getUrlPathByPlatform(platform)}`;
    return http.post(url, payload, config);
  },

  /* COMMUNITY OVERVIEW V2 APIS */
  async getOverviewStatsV2(params, config) {
    const url = `/overview_stats`;
    return http.get(url, { ...config, params });
  },
  async getAggregateSentimentByPlatformV2(payload, config) {
    const url = `/sentiment_distribution_stats`;
    return http.post(url, payload, config);
  },
  async getVolumeTimeSeriesStatsV2(payload, config) {
    const url = `/volume_time_series`;
    return http.post(url, payload, config);
  },
  async getSentimentTimeSeriesV2(payload, config) {
    const url = `/sentiment_time_series`;
    return http.post(url, payload, config);
  },

  async getWordCloudTopKeywordsV2(payload, config) {
    const url = `/top_keywords`;
    return http.post(url, payload, config);
  },

  /* COMMUNITY TEAM ACTIVITY */
  async getTeamActivities(payload, config) {
    const url = `/team_activities`;
    return http.post(url, payload, { ...config, camelizeKeys: false });
  },

  //
  // COMMUNITY INTERACTIONS
  //
  async getCommunityInteractions(params, config = {}) {
    const url = params?.next ?? '/community_interactions';
    return http.get(url, { ...config, params: { ...params, next: undefined } });
  },
  async updateCommunityInteraction(id, payload, config = {}) {
    return http.patch(`/community_interactions/${id}`, payload, config);
  },
  async bulkUpdateCommunityInteractions(payload, params = {}, config = {}) {
    return http.patch('/community_interactions', payload, { ...config, params });
  },
  async getTags(params, config = {}) {
    return http.get('/tags', { ...config, params });
  },
  async createTag(payload, config = {}) {
    return http.post('/tags', payload, config);
  },
  async deleteTag(tagId, config = {}) {
    return http.delete(`/tags/${tagId}`, config);
  },

  async createAssignment(brandId, interactionId, payload, config = {}) {
    const url = `/brands/${brandId}/community_interactions/${interactionId}/assignments`;
    return http.post(url, payload, config);
  },
  async getAssignees({ brandIds, sourceUpdatedAfter, sourceUpdatedBefore }, config = {}) {
    return http.get('/assignees', {
      ...config,
      params: { brandIds, sourceUpdatedAfter, sourceUpdatedBefore },
    });
  },

  //
  // SALESFORCE INTEGRATION
  //

  async getCaseById(params, config = {}) {
    return http.get('/integrations/tickets', {
      ...config,
      params,
    });
  },

  async createSalesforceCase(payload, config = {}) {
    return http.post('/integrations/tickets', payload, config);
  },
  async sendPrivateReply({ commentId, text }, config = {}) {
    return http.post(`/comments/${commentId}/private_reply`, { text }, config);
  },
};
